/** Severity helpers for product/treatment recommendations (lower score = worse skin). */

export const GOOD_SCORE_THRESHOLD = 89;

const SCORE_KEY_ALIASES = {
  hydration: 'Hydration',
  oxygen: 'Oxygen',
  smoothness: 'Smoothness',
  dullness: 'Dullness',
  uneven_skintone: 'UnevenSkin',
  unevenskintone: 'UnevenSkin',
  'uneven skintone': 'UnevenSkin',
  'uneven skin': 'UnevenSkin',
  even_tone: 'UnevenSkin',
  eventone: 'UnevenSkin',
  acne: 'Acne',
  pigmentation: 'Pigmentation',
  wrinkles: 'Wrinkles',
  wrinkle: 'Wrinkles',
  'dark circle': 'Dark Circle',
  dark_circle: 'Dark Circle',
  dark_circles: 'Dark Circle',
  'dark circles': 'Dark Circle',
  'dark spots': 'Dark Spots',
  dark_spots: 'Dark Spots',
  'eye bags': 'Eye Bags',
  eye_bags: 'Eye Bags',
  'open pores': 'Open Pores',
  open_pores: 'Open Pores',
  'clogged pores': 'Clogged Pores',
  clogged_pores: 'Clogged Pores',
  'dry skin': 'Dry Skin',
  dry_skin: 'Dry Skin',
  'oily skin': 'Oily Skin',
  oily_skin: 'Oily Skin',
  "crow's feet": "Crow's Feet",
  crows_feet: "Crow's Feet",
  red_spot: 'Red Spot',
  'red spot': 'Red Spot',
  blackheads: 'Blackheads',
  whiteheads: 'Whiteheads',
};

export function normalizeConditionKey(name) {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/[-\s]+/g, '_');
}

export function canonicalConditionName(name) {
  const raw = String(name || '').trim();
  if (!raw) return '';
  const key = normalizeConditionKey(raw);
  return SCORE_KEY_ALIASES[key] || SCORE_KEY_ALIASES[raw.toLowerCase()] || raw;
}

export function mergeConditionScoreMaps(skinScore = {}) {
  const merged = {};
  const maps = [
    skinScore.condition_scores,
    skinScore.skin_label_scores,
    skinScore.attribute_scores,
  ];

  maps.forEach((mapObj) => {
    Object.entries(mapObj || {}).forEach(([key, value]) => {
      if (value == null || Number.isNaN(Number(value))) return;
      const canonical = canonicalConditionName(key);
      merged[canonical] = Number(value);
      merged[key] = Number(value);
    });
  });

  // Top-level score_* fields from skin_score.py (when maps omit a concern).
  const topLevel = {
    Hydration: skinScore.score_hydration,
    Dullness: skinScore.score_dullness,
    Smoothness: skinScore.score_smoothness,
    Oxygen: skinScore.score_oxygen,
    UnevenSkin: skinScore.score_uneven_skintone,
  };
  Object.entries(topLevel).forEach(([name, value]) => {
    if (value == null || Number.isNaN(Number(value))) return;
    if (merged[name] != null) return;
    merged[name] = Number(value);
  });

  return merged;
}

export function inferScoresFromDetections(detections = [], baseScores = {}) {
  const merged = { ...baseScores };
  const bestConfidenceByCondition = new Map();

  (Array.isArray(detections) ? detections : []).forEach((det) => {
    const name = canonicalConditionName(det?.class_name || det?.className || det?.name);
    if (!name || name === 'Unknown' || name === 'Mole') return;
    const confidence = Number(det?.confidence);
    if (Number.isNaN(confidence) || confidence <= 0) return;
    const prev = bestConfidenceByCondition.get(name) || 0;
    if (confidence > prev) bestConfidenceByCondition.set(name, confidence);
  });

  bestConfidenceByCondition.forEach((confidence, name) => {
    if (getConditionScore(name, merged) != null) return;
    const healthScore = Math.max(30, Math.min(88, 100 - confidence * 45));
    merged[name] = Math.round(healthScore);
  });

  return merged;
}

export function getConditionScore(name, conditionScores = {}) {
  if (!name || !conditionScores || typeof conditionScores !== 'object') return null;
  const canonical = canonicalConditionName(name);
  if (Object.prototype.hasOwnProperty.call(conditionScores, canonical) && conditionScores[canonical] != null) {
    return Number(conditionScores[canonical]);
  }
  if (Object.prototype.hasOwnProperty.call(conditionScores, name) && conditionScores[name] != null) {
    return Number(conditionScores[name]);
  }
  const target = normalizeConditionKey(canonical);
  for (const [key, value] of Object.entries(conditionScores)) {
    if (value == null) continue;
    if (normalizeConditionKey(key) === target) return Number(value);
  }
  return null;
}

export function buildRecommendableConditions(
  detectedConditions = [],
  conditionScores = {},
  detections = []
) {
  const mergedScores = inferScoresFromDetections(detections, conditionScores);
  const seen = new Set();
  const out = [];
  const candidates = [
    ...(Array.isArray(detectedConditions) ? detectedConditions : []),
    ...Object.keys(mergedScores || {}),
    ...(Array.isArray(detections) ? detections.map((d) => d?.class_name || d?.className || d?.name) : []),
  ];

  candidates.forEach((raw) => {
    const name = canonicalConditionName(raw);
    if (!name || name === 'Unknown' || name === 'Mole') return;
    const key = normalizeConditionKey(name);
    if (seen.has(key)) return;
    const score = getConditionScore(name, mergedScores);
    if (score == null || Number.isNaN(score) || score >= GOOD_SCORE_THRESHOLD) return;
    seen.add(key);
    out.push(name);
  });

  return out;
}

export function buildRecommendationPayloadScores(skinScore = {}, detections = []) {
  return inferScoresFromDetections(detections, mergeConditionScoreMaps(skinScore));
}
